import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";

/** COMPONENT */
import { Box, MenuItem, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import AutocompleteSelect from "../../../component/select/autoCompleteSelect";
import FilterSelect from "../../../component/select/filterSelect";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import StallTable from "../components/StallTable";

/** API */
import InheritanceTransferApi, {
  HeirOption,
  OwnerStall,
  OwnerSuggestion,
} from "../../../api/inheritance-transfer/inheritance-transfer.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import { InheritanceCreatePrefill } from "../../../constants/inheritanceTransfer";
import { getApiErrorMessage } from "../../../utils/api-error.utils";

/** STYLE */
import * as Style from "../inheritanceTransfer.style";

type Props = {
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว */
  onSuccess: () => void;
  /** เลือกผู้โอน/ผู้รับโอนไว้ให้ก่อน (มาจากหน้าจัดการข้อมูลลูกค้า หลังรับเอกสารผู้รับมรดก) */
  prefill?: InheritanceCreatePrefill;
};

/** prefill มีแค่ key + ชื่อ — field ที่เหลือใช้แค่ตอน render รายการ suggestion จึงเติมค่าว่างได้ */
const ownerFromPrefill = (prefill?: InheritanceCreatePrefill): OwnerSuggestion | null =>
  prefill
    ? {
        userId: prefill.userId_owner,
        customerId: prefill.customerId_owner,
        owner_fullname: prefill.owner_fullname,
        idCard: "",
        mobilePhone: "",
        heir_count: 0,
        stall_count: 0,
      }
    : null;

/** ดีไซน์แสดง suggestion 6 รายการ */
const OWNER_SUGGESTION_LIMIT = 6;
const HEIR_EMPTY_TEXT = "เลือกผู้โอนก่อน หรือไม่มีผู้รับมรดก";

export default function ModalInheritanceCreate({ onClose, onSuccess, prefill }: Props) {
  const [ownerOptions, setOwnerOptions] = useState<OwnerSuggestion[]>([]);
  const [isLoadingOwner, setIsLoadingOwner] = useState<boolean>(false);
  /** ใส่ชื่อผู้โอนจาก prefill ไว้ในช่อง — debounce search จะโหลด suggestion ที่มีคนนี้มาเอง */
  const [searchOwner, setSearchOwner] = useState<string>(() => prefill?.owner_fullname || "");
  const [owner, setOwner] = useState<OwnerSuggestion | null>(() => ownerFromPrefill(prefill));

  const [heirOptions, setHeirOptions] = useState<HeirOption[]>([]);
  const [heirId, setHeirId] = useState<number | "">("");
  /** ผู้รับโอนจาก prefill รอเลือกให้หลังโหลดรายชื่อผู้รับมรดกเสร็จ — ใช้ครั้งเดียว */
  const pendingHeirIdRef = useRef<number | undefined>(prefill?.id_customer_agent);

  const [stalls, setStalls] = useState<OwnerStall[]>([]);
  const [isLoadingOwnerData, setIsLoadingOwnerData] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน — สำคัญเพราะ 1 ผู้โอนมีคำร้องค้างได้ใบเดียว */
  const isSubmittingRef = useRef<boolean>(false);

  const loadOwnerOptions = useCallback(async (keyword: string) => {
    setIsLoadingOwner(true);
    try {
      const res = await InheritanceTransferApi.ownerSearch({
        keyword: keyword || undefined,
        limit: OWNER_SUGGESTION_LIMIT,
        page: 1,
        only_has_stall: 1,
      });
      setOwnerOptions(res.data?.data?.items || []);
    } catch (error) {
      setOwnerOptions([]);
    } finally {
      setIsLoadingOwner(false);
    }
  }, []);

  /** debounce 300ms กันยิง search ทุก keystroke */
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOwnerOptions(searchOwner);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchOwner, loadOwnerOptions]);

  /** เลือกผู้โอนแล้วโหลดผู้รับมรดกกับแผงค้าพร้อมกัน — สองชุดนี้ผูกกับผู้โอนคนเดียวกันเสมอ */
  useEffect(() => {
    let isMounted = true;

    if (!owner?.userId) {
      setHeirOptions([]);
      setStalls([]);
      return;
    }

    const loadOwnerData = async () => {
      setIsLoadingOwnerData(true);
      try {
        const [heirRes, stallRes] = await Promise.all([
          // customerId เป็น null = ยังไม่มีข้อมูลลูกค้า ระบุผู้รับมรดกไม่ได้ ไม่ต้องยิง
          owner.customerId ? InheritanceTransferApi.ownerHeir(owner.userId) : Promise.resolve(null),
          InheritanceTransferApi.ownerStall(owner.userId),
        ]);
        if (!isMounted) return;
        setHeirOptions(heirRes?.data?.data || []);
        setStalls(stallRes.data?.data || []);
      } catch (error) {
        if (!isMounted) return;
        setHeirOptions([]);
        setStalls([]);
        notiError(getApiErrorMessage(error, "ดึงข้อมูลของผู้โอนไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoadingOwnerData(false);
      }
    };

    loadOwnerData();
    return () => {
      isMounted = false;
    };
  }, [owner]);

  /** ถ้าไม่พบในรายการ (เช่น ยังไม่ได้รับเอกสาร) ปล่อยให้เลือกเอง */
  useEffect(() => {
    const pendingId = pendingHeirIdRef.current;
    if (!pendingId || heirOptions.length === 0) return;
    pendingHeirIdRef.current = undefined;
    if (heirOptions.some((heir) => heir.id_customer_agent === pendingId)) setHeirId(pendingId);
  }, [heirOptions]);

  /** เปลี่ยนผู้โอนต้องล้างผู้รับโอนเดิมเสมอ ไม่งั้นจะส่ง id_customer_agent ของคนอื่นไป */
  const handleChangeOwner = (value: OwnerSuggestion | null) => {
    pendingHeirIdRef.current = undefined;
    setOwner(value);
    setHeirId("");
  };

  const onSubmit = async () => {
    if (!owner?.userId || !heirId || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // ผู้โอนอ้างด้วย userId ไม่ใช่ customerId — backend หา customerId เองจาก userId
      const res = await InheritanceTransferApi.create({
        userId_owner: owner.userId,
        id_customer_agent: Number(heirId),
      });
      notiSuccess(`สร้างคำร้องโอนมรดกสำเร็จ ${res.data?.data?.inheritance_no || ""}`.trim());
      onSuccess();
    } catch (error) {
      // backend ตรวจ 5 เคส (ไม่พบผู้โอน / ไม่มีข้อมูลลูกค้า / heir ไม่ใช่ของผู้โอน / มีคำร้องค้าง / ไม่มีแผงค้า)
      // ทุกเคสคืนข้อความไทยมาแล้ว แสดงตรงๆ ได้เลย
      notiError(getApiErrorMessage(error, "สร้างคำร้องโอนมรดกไม่สำเร็จ"));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const selectedHeir = heirOptions.find((heir) => heir.id_customer_agent === heirId);

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
    >
      <Row>
        <Col md={6} xs={12} className="my-2">
          <AutocompleteSelect
            labelId="inheritance-owner"
            heading="ผู้โอน (ผู้ถือสิทธิเดิม)"
            required
            placeholder="ค้นหาชื่อ หรือเลขประจำตัวประชาชน"
            options={ownerOptions}
            disabled={isSubmitting}
            getOptionLabel={(option: any) => option?.owner_fullname || ""}
            // ต้องระบุเอง ไม่งั้น client กรองผลที่ค้นด้วยเลขบัตร (ที่ server หาเจอ) ทิ้ง
            // เพราะ default stringify ของ wrapper ใช้ getOptionLabel = ชื่อ อย่างเดียว
            filterOptions={(option: any) =>
              `${option?.owner_fullname || ""} ${option?.idCard || ""}`
            }
            renderOption={(optionProps: any, option: any) => (
              <li {...optionProps} key={option?.userId}>
                <Box>
                  <Typography sx={{ fontSize: 16 }}>{option?.owner_fullname || "-"}</Typography>
                  {/* เลขบัตรถูก mask จาก backend มาแล้ว ห้าม mask ซ้ำ */}
                  <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                    {`เลขประจำตัวประชาชน ${option?.idCard || "-"} · ${option?.stall_count ?? 0} แผงค้า`}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option: any, value: any) => option?.userId === value?.userId}
            inputValue={searchOwner || ""}
            onInputChange={(event: any, value: any) => setSearchOwner(value)}
            value={owner}
            onChange={(event: any, value: any) => handleChangeOwner(value)}
            loading={isLoadingOwner}
            noOptionsText="ไม่พบรายชื่อ"
          />
        </Col>
        <Col md={6} xs={12} className="my-2">
          <FilterSelect
            labelId="inheritance-heir"
            selectId="inheritance-heir"
            heading="ผู้รับโอน (ผู้รับมรดก)"
            required
            value={heirId}
            disabled={isSubmitting || isLoadingOwnerData || heirOptions.length === 0}
            onchange={(event: any) => setHeirId(event.target.value)}
            renderValue={() =>
              selectedHeir ? (
                selectedHeir.heir_fullname
              ) : (
                <span style={{ color: colors.black_60 }}>
                  {owner ? HEIR_EMPTY_TEXT : "เลือกผู้รับโอน"}
                </span>
              )
            }
            options={heirOptions.map((heir: HeirOption) => (
              <MenuItem key={heir.id_customer_agent} value={heir.id_customer_agent}>
                <Box>
                  <Typography sx={{ fontSize: 16 }}>{heir.heir_fullname || "-"}</Typography>
                  <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                    {`${heir.agent_type_name || "ผู้รับมรดก"} · เลขประจำตัวประชาชน ${heir.idCard || "-"}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          />
          {/* ทั้งกรณีไม่มีผู้รับมรดก และกรณีผู้โอนไม่มีข้อมูลลูกค้า ใช้ข้อความเดียวกันตามดีไซน์ */}
          {owner && !isLoadingOwnerData && heirOptions.length === 0 && (
            <Typography sx={{ fontSize: 14, color: colors.red, mt: 1 }}>
              {`${HEIR_EMPTY_TEXT} — เพิ่มได้ที่เมนูจัดการข้อมูลผู้ใช้งาน > เพิ่มผู้แทน (ประเภทผู้รับมรดก)`}
            </Typography>
          )}
        </Col>
      </Row>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 2, mb: 1, flexWrap: "wrap" }}>
        <Style.SectionTitle>ข้อมูลแผงค้าของผู้โอน</Style.SectionTitle>
        {owner && (
          <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
            {`${stalls.length} แผงค้า · ${owner.owner_fullname || "-"}`}
          </Typography>
        )}
      </Box>

      <StallTable
        stalls={stalls}
        textEmptyData={owner ? "ผู้โอนรายนี้ไม่มีแผงค้าที่อยู่ระหว่างสัญญาเช่า" : "เลือกผู้โอนเพื่อแสดงข้อมูลแผงค้า"}
      />
    </Box>
  );

  return (
    <>
      {(isSubmitting || isLoadingOwnerData) && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        size="xl"
        closeButton
        alignFooter="end"
        footerInline
        title="ทำรายการโอนมรดก"
        component={renderBody()}
        textBtnCancel="ยกเลิก"
        onReset={onClose}
        textBtnConfirm="ยืนยัน"
        onSubmit={onSubmit}
        disabledSubmit={!owner || !heirId || isSubmitting}
        onClose={onClose}
      />
    </>
  );
}
